import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, ChevronDown, ChevronRight, CalendarClock, DoorOpen, AlertTriangle, Users, UserPlus, UserCheck, RotateCcw, Eye, Archive } from "lucide-react";
import { motion } from "framer-motion";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api, { formatApiErrorDetail } from "../lib/api";
import { fmtCurrency, fmtPct, fmtPeriod, fmtPeriodShort, buildPeriodOptions } from "../lib/format";
import Header from "../components/dashboard/Header";
import { useAuth } from "../context/AuthContext";
import DefaultViewSelect from "../components/DefaultViewSelect";
import AskBar from "../components/dashboard/AskBar";
import { AvailabilityPanel } from "../components/AvailabilityPanel";
import { CrmFillBanner, pendingWeeks } from "../components/sales/CrmFill";

const CATEGORIES = [
  "Office Space", "Dedicated", "Open/Flex Desks - Coworking",
  "Event Space", "Virtual Mail",
];
const emptyCats = () => CATEGORIES.map((name) => ({ name, tours_booked: 0, tours_completed: 0, won_deals: 0 }));

// --- Month roll-up helper: previous YYYY-MM ---
const prevMonth = (ym) => {
  const d = new Date(`${ym}-01T00:00:00`);
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 7);
};

function RecapCard({ label, value, sub, delta, tone, testid, index }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: index * 0.05 }}
      data-testid={testid} className="bg-brand-surface p-6 flex flex-col justify-between min-h-[128px]">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs uppercase tracking-[0.15em] text-brand-tertiary">{label}</p>
        {delta != null && delta !== 0 && (
          <span data-testid={`${testid}-delta`} className={`text-[11px] font-mono ${delta > 0 ? "text-brand-pos" : "text-brand-neg"}`}>
            {delta > 0 ? "+" : ""}{fmtCurrency(delta)} vs last mo
          </span>
        )}
      </div>
      <div className="mt-4">
        <p className={`font-mono text-2xl lg:text-3xl tracking-tight ${tone || "text-brand-ink"}`}>{value}</p>
        <p className="text-[11px] mt-1 text-brand-tertiary">{sub}</p>
      </div>
    </motion.div>
  );
}

function MonthlyRollup({ months, testid, columns, leftCols = 1, renderSummary, renderRow, fetchDetail }) {
  const [open, setOpen] = useState({});
  const [cache, setCache] = useState({});
  const [loading, setLoading] = useState({});
  if (!months.length) return null;
  const toggle = async (key) => {
    const willOpen = !open[key];
    setOpen((o) => ({ ...o, [key]: willOpen }));
    if (willOpen && fetchDetail && cache[key] === undefined) {
      setLoading((l) => ({ ...l, [key]: true }));
      try { const items = await fetchDetail(key); setCache((c) => ({ ...c, [key]: items || [] })); }
      catch { setCache((c) => ({ ...c, [key]: [] })); }
      finally { setLoading((l) => ({ ...l, [key]: false })); }
    }
  };
  return (
    <div className="border-t border-brand-border" data-testid={testid}>
      <p className="px-6 pt-3 pb-1 text-[10px] uppercase tracking-[0.15em] text-brand-tertiary">Earlier months · rolled up</p>
      {months.map((mth) => {
        const isOpen = !!open[mth.key];
        const items = fetchDetail ? cache[mth.key] : mth.items;
        return (
          <div key={mth.key} className="border-b border-brand-border last:border-0">
            <button onClick={() => toggle(mth.key)} data-testid={`${testid}-row-${mth.key}`}
              className="w-full flex items-center gap-3 px-6 py-3 text-left hover:bg-brand-hover transition-colors">
              <ChevronRight className={`h-4 w-4 shrink-0 text-brand-tertiary transition-transform ${isOpen ? "rotate-90" : ""}`} />
              <span className="font-medium text-brand-ink w-24 shrink-0">{fmtPeriod(mth.key)}</span>
              <span className="text-sm min-w-0 truncate">{renderSummary(mth)}</span>
            </button>
            {isOpen && (
              <div className="overflow-x-auto bg-brand-hover/30 border-t border-brand-border" data-testid={`${testid}-detail-${mth.key}`}>
                {loading[mth.key] ? (
                  <p className="px-6 py-4 text-xs text-brand-tertiary">Loading…</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-brand-tertiary border-b border-brand-border">
                        {columns.map((h, i) => (
                          <th key={h || `c${i}`} className={`font-medium uppercase tracking-[0.1em] text-[10px] px-4 py-2.5 ${i < leftCols ? `text-left${i === 0 ? " px-6" : ""}` : "text-right"}`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>{(items || []).map(renderRow)}</tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SalesAvailability({ location, onSelectLocation }) {
  const [open, setOpen] = useState(() => localStorage.getItem("bond-sales-avail-open") !== "0");
  const toggle = () => setOpen((v) => { localStorage.setItem("bond-sales-avail-open", v ? "0" : "1"); return !v; });
  const all = location === "All Locations";
  return (
    <section className="border border-brand-border rounded-xl bg-brand-surface/60" data-testid="sales-availability">
      <button onClick={toggle} data-testid="sales-availability-toggle" className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left">
        <span className="flex items-center gap-2 min-w-0">
          <DoorOpen className="h-4 w-4 text-brand-ink shrink-0" />
          <span className="text-brand-ink font-medium">Availability &amp; Floor Plan</span>
          <span className="text-xs text-brand-tertiary truncate hidden sm:inline">{all ? "Vacancy by location — pick one to see its floor plan" : `Live from Yardi · ${location}`}</span>
        </span>
        <ChevronDown className={`h-4 w-4 text-brand-tertiary shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5" data-testid="sales-availability-body">
          <AvailabilityPanel location={all ? "all" : location} onSelectLocation={all ? onSelectLocation : undefined} floorplanFirst />
        </div>
      )}
    </section>
  );
}

function Select({ value, onChange, options, testid, label }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-1.5">{label}</label>
      <div className="relative">
        <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
          className="appearance-none bg-brand-surface border border-brand-border hover:border-brand-borderStrong text-sm text-brand-ink rounded-full pl-4 pr-10 py-2.5 focus:outline-none focus:border-brand-primary transition-colors duration-200 min-w-[190px] cursor-pointer">
          {options.map((o, i) => <option key={`${o.value}-${i}`} value={o.value} disabled={o.disabled}>{o.label}</option>)}
        </select>
        <ChevronDown className="h-4 w-4 text-brand-tertiary absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, highlight, index }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: index * 0.05 }}
      data-testid={`sales-kpi-${label.toLowerCase().replace(/\s/g, "-")}`}
      className={`p-6 flex flex-col justify-between min-h-[120px] ${highlight ? "bg-brand-ink text-brand-page" : "bg-brand-surface"}`}>
      <p className={`text-xs uppercase tracking-[0.15em] ${highlight ? "text-brand-page/60" : "text-brand-tertiary"}`}>{label}</p>
      <div className="mt-3">
        <p className={`font-mono text-2xl lg:text-3xl tracking-tight ${highlight ? "text-brand-page" : "text-brand-ink"}`}>{value}</p>
        {sub && <p className={`text-[11px] mt-1 ${highlight ? "text-brand-page/50" : "text-brand-tertiary"}`}>{sub}</p>}
      </div>
    </motion.div>
  );
}

function TrendTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-brand-surface border border-brand-borderStrong shadow-sm px-4 py-3 text-sm">
      <p className="font-serif text-brand-ink mb-2">{fmtPeriod(label)}</p>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-6">
          <span className="text-brand-muted flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: p.color }} />{p.name}</span>
          <span className="font-mono text-brand-ink">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

function summarize(months) {
  const tb = months.reduce((a, m) => a + m.tours_booked, 0);
  const tc = months.reduce((a, m) => a + m.tours_completed, 0);
  const wd = months.reduce((a, m) => a + m.won_deals, 0);
  const rev = months.reduce((a, m) => a + (m.actual_revenue || 0), 0);
  return { tb, tc, wd, rev, lead: tb ? tc / tb : null, close: tb ? wd / tb : null };
}

function summarizeCategories(months) {
  const map = {};
  CATEGORIES.forEach((n) => (map[n] = { name: n, booked: 0, completed: 0, won: 0 }));
  months.forEach((m) => (m.categories || []).forEach((c) => {
    if (map[c.name]) {
      map[c.name].booked += c.tours_booked || 0;
      map[c.name].completed += c.tours_completed || 0;
      map[c.name].won += c.won_deals || 0;
    }
  }));
  return CATEGORIES.map((n) => map[n]);
}

function Editor({ initial, isNew, defaultLocation, locations, onClose, onSaved }) {
  const [period, setPeriod] = useState(initial?.period || "");
  const [location, setLocation] = useState(initial?.location || defaultLocation);
  const [actual, setActual] = useState(initial?.actual_revenue ?? "");
  const [target, setTarget] = useState(initial?.target_revenue ?? "");
  const [cats, setCats] = useState(() => {
    const base = emptyCats();
    (initial?.categories || []).forEach((c) => {
      const t = base.find((b) => b.name === c.name);
      if (t) { t.tours_booked = c.tours_booked || 0; t.tours_completed = c.tours_completed || 0; t.won_deals = c.won_deals || 0; }
    });
    return base;
  });
  const [saving, setSaving] = useState(false);
  const setCat = (i, f, v) => setCats((p) => p.map((c, idx) => idx === i ? { ...c, [f]: v === "" ? 0 : parseInt(v, 10) || 0 } : c));

  const save = async () => {
    if (!period) { toast.error("Please choose a month."); return; }
    if (!location) { toast.error("Please choose a location."); return; }
    setSaving(true);
    const body = { period, location, actual_revenue: actual === "" ? null : parseFloat(actual), target_revenue: target === "" ? null : parseFloat(target), categories: cats };
    try {
      if (isNew) await api.post("/sales", body);
      else await api.put(`/sales/${period}`, body);
      toast.success(isNew ? "Month added" : "Sales updated", { description: `${location} · ${fmtPeriod(period)}` });
      onSaved(); onClose();
    } catch (err) {
      toast.error("Save failed", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-ink/40 backdrop-blur-sm" data-testid="sales-editor">
      <div className="bg-brand-surface border border-brand-borderStrong w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-brand-border sticky top-0 bg-brand-surface">
          <div>
            <p className="text-xs uppercase tracking-[0.15em] text-brand-tertiary">{isNew ? "Add Month" : "Edit"}</p>
            <p className="font-serif text-xl text-brand-ink">{isNew ? "New sales month" : `${initial?.location} · ${fmtPeriod(period)}`}</p>
          </div>
          <button onClick={onClose} data-testid="sales-editor-close" className="p-1.5 text-brand-tertiary hover:text-brand-ink"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            {isNew ? (
              <>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-1.5">Location</label>
                  <select value={location} onChange={(e) => setLocation(e.target.value)} data-testid="sales-editor-location"
                    className="w-full bg-brand-page border border-brand-border rounded-md px-3 py-2.5 text-sm text-brand-ink focus:outline-none focus:border-brand-primary">
                    {locations.filter((l) => l !== "All Locations").map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-1.5">Month</label>
                  <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="sales-editor-month"
                    className="w-full bg-brand-page border border-brand-border rounded-md px-3 py-2.5 text-sm text-brand-ink focus:outline-none focus:border-brand-primary" />
                </div>
              </>
            ) : null}
            <div>
              <label className="block text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-1.5">Target Revenue</label>
              <input type="number" value={target} onChange={(e) => setTarget(e.target.value)} data-testid="sales-editor-target" placeholder="0"
                className="w-full bg-brand-page border border-brand-border rounded-md px-3 py-2.5 text-sm font-mono text-brand-ink focus:outline-none focus:border-brand-primary" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-1.5">Actual Revenue</label>
              <input type="number" value={actual} onChange={(e) => setActual(e.target.value)} data-testid="sales-editor-actual" placeholder="0"
                className="w-full bg-brand-page border border-brand-border rounded-md px-3 py-2.5 text-sm font-mono text-brand-ink focus:outline-none focus:border-brand-primary" />
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-2">Pipeline by Product</p>
            <div className="border border-brand-border rounded-md overflow-hidden">
              <div className="grid grid-cols-[1.6fr_1fr_1fr_1fr] bg-brand-hover text-[10px] uppercase tracking-[0.1em] text-brand-tertiary">
                <span className="px-3 py-2.5">Product</span>
                <span className="px-3 py-2.5 text-right border-l border-brand-border">Booked</span>
                <span className="px-3 py-2.5 text-right border-l border-brand-border">Completed</span>
                <span className="px-3 py-2.5 text-right border-l border-brand-border">Won</span>
              </div>
              {cats.map((c, i) => (
                <div key={c.name} className="grid grid-cols-[1.6fr_1fr_1fr_1fr] border-t border-brand-border items-center">
                  <span className="px-3 py-2 text-sm text-brand-ink">{c.name}</span>
                  {["tours_booked", "tours_completed", "won_deals"].map((f) => (
                    <input key={f} type="number" min="0" value={c[f]} onChange={(e) => setCat(i, f, e.target.value)} data-testid={`sales-cat-${i}-${f}`}
                      className="w-full px-3 py-2 text-sm font-mono text-right text-brand-ink bg-transparent border-l border-brand-border focus:outline-none focus:bg-brand-hover" />
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={onClose} className="px-5 py-2.5 text-sm text-brand-muted border border-brand-border hover:border-brand-borderStrong">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="sales-editor-save"
              className="px-6 py-2.5 text-sm bg-brand-primary text-brand-page hover:bg-brand-primaryHover active:scale-[0.98] transition-all disabled:opacity-60">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const mondayOf = (s) => {
  const d = new Date(s + "T00:00:00");
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  return d.toISOString().slice(0, 10);
};
const fmtWeek = (s) => {
  if (!s) return "";
  const start = new Date(s + "T00:00:00");
  const end = new Date(start); end.setDate(end.getDate() + 4);
  const o = { month: "short", day: "numeric" };
  return `${start.toLocaleDateString("en-US", o)} – ${end.toLocaleDateString("en-US", o)}`;
};

function useOfficeCatalog(location) {
  const [offices, setOffices] = useState([]);
  useEffect(() => {
    if (!location || location === "All Locations") { setOffices([]); return; }
    api.get("/proposals/offices", { params: { location } })
      .then(({ data }) => setOffices(data.offices || [])).catch(() => setOffices([]));
  }, [location]);
  return offices;
}

function OfficeOptions({ offices, id }) {
  return (
    <datalist id={id}>
      {offices.map((o) => (
        <option key={o.id || o.office_number} value={o.office_number}>
          {[o.desks ? `${o.desks} desks` : null, o.monthly_price ? `$${o.monthly_price}/mo` : null].filter(Boolean).join(" · ")}
        </option>
      ))}
    </datalist>
  );
}

function WeeklyEditor({ initial, isNew, location, onClose, onSaved }) {
  const [weekDate, setWeekDate] = useState(initial?.week_start || "");
  const [cats, setCats] = useState(() => {
    const base = emptyCats();
    (initial?.categories || []).forEach((c) => {
      const t = base.find((b) => b.name === c.name);
      if (t) { t.tours_booked = c.tours_booked || 0; t.tours_completed = c.tours_completed || 0; t.won_deals = c.won_deals || 0; }
    });
    return base;
  });
  const [deals, setDeals] = useState(() =>
    (initial?.closed_deals || []).map((d) => ({
      company: d.company || "", office: d.office_number || "", product: d.product || "", monthly_price: d.monthly_price ?? "", term: d.term || "",
    }))
  );
  const [notes, setNotes] = useState(initial?.notes || "");
  const [saving, setSaving] = useState(false);
  const [source, setSource] = useState(initial?.source || "manual");
  const [crmInfo, setCrmInfo] = useState(null);
  const [crmLoading, setCrmLoading] = useState(false);
  const reqId = useRef(0);
  const dirty = useRef(false);
  const fillFromCrm = useCallback(async (dateStr, manual = true) => {
    if (!dateStr) { toast.error("Pick a week first."); return; }
    if (!manual && dirty.current) return;
    const id = ++reqId.current;
    setCrmLoading(true);
    try {
      const { data } = await api.get("/sales/weekly/crm-draft", { params: { location, week_start: mondayOf(dateStr) } });
      if (id !== reqId.current || (!manual && dirty.current)) return;
      dirty.current = false;
      setCats(data.categories.map((c) => ({ ...c })));
      setDeals((prev) => {
        const have = new Set(prev.map((d) => d.company.trim().toLowerCase()));
        const add = data.closed_deals.filter((d) => !have.has(d.company.toLowerCase()))
          .map((d) => ({ company: d.company, office: d.office_number || "", product: d.product || "", monthly_price: d.monthly_price || "", term: d.term || "" }));
        return [...prev, ...add];
      });
      setCrmInfo(data); setSource("crm");
    } catch (e) { if (id === reqId.current) toast.error("Couldn't pull from the CRM", { description: e.userMessage }); }
    finally { if (id === reqId.current) setCrmLoading(false); }
  }, [location]);
  useEffect(() => { if (isNew && weekDate) fillFromCrm(weekDate, false); }, [isNew, weekDate, fillFromCrm]);
  const setCat = (i, f, v) => { dirty.current = true; setCats((p) => p.map((c, idx) => idx === i ? { ...c, [f]: v === "" ? 0 : parseInt(v, 10) || 0 } : c)); };
  const addDeal = () => setDeals((p) => [...p, { company: "", office: "", product: "", monthly_price: "", term: "" }]);
  const offices = useOfficeCatalog(location);
  const setDealOffice = (i, v) => setDeals((p) => p.map((d, idx) => {
    if (idx !== i) return d;
    const m = offices.find((o) => o.office_number === v);
    return { ...d, office: v, monthly_price: (m && (d.monthly_price === "" || d.monthly_price == null)) ? (m.monthly_price ?? "") : d.monthly_price };
  }));
  const setDeal = (i, f, v) => { dirty.current = true; setDeals((p) => p.map((d, idx) => idx === i ? { ...d, [f]: v } : d)); };
  const removeDeal = (i) => setDeals((p) => p.filter((_, idx) => idx !== i));
  const dealsMrr = deals.reduce((a, d) => a + (parseFloat(d.monthly_price) || 0), 0);

  const save = async () => {
    if (!weekDate) { toast.error("Pick a week."); return; }
    const cleanDeals = deals
      .filter((d) => d.company.trim())
      .map((d) => ({ company: d.company.trim(), office_number: d.office.trim() || null, product: d.product || null, monthly_price: d.monthly_price === "" ? 0 : parseFloat(d.monthly_price) || 0, term: d.term.trim() || null }));
    const week_start = mondayOf(weekDate);
    setSaving(true);
    const body = { week_start, location, new_business_revenue: cleanDeals.length ? dealsMrr : null, notes: notes.trim() || null, categories: cats, closed_deals: cleanDeals, source };
    try {
      if (isNew) await api.post("/sales/weekly", body);
      else await api.put(`/sales/weekly/${initial.id}`, body);
      toast.success(isNew ? "Week confirmed" : "Week updated", { description: `${location} · week of ${week_start}` });
      onSaved(); onClose();
    } catch (err) {
      toast.error("Save failed", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-ink/40 backdrop-blur-sm" data-testid="weekly-editor">
      <div className="bg-brand-surface border border-brand-borderStrong w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-brand-border sticky top-0 bg-brand-surface">
          <div>
            <p className="text-xs uppercase tracking-[0.15em] text-brand-tertiary">{isNew ? "Add Week" : "Edit Week"}</p>
            <p className="font-serif text-xl text-brand-ink">{location} · weekly entry</p>
          </div>
          <button onClick={onClose} data-testid="weekly-editor-close" className="p-1.5 text-brand-tertiary hover:text-brand-ink"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-1.5">Week (any day — snaps to Mon–Fri)</label>
              <input type="date" value={weekDate} onChange={(e) => setWeekDate(e.target.value)} data-testid="weekly-editor-date"
                className="w-full bg-brand-page border border-brand-border rounded-md px-3 py-2.5 text-sm text-brand-ink focus:outline-none focus:border-brand-primary" />
              {weekDate && <p className="text-[11px] text-brand-tertiary mt-1">Week of {fmtWeek(mondayOf(weekDate))} → rolls into {mondayOf(weekDate).slice(0, 7)}</p>}
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-1.5">New Business Closed ($/mo)</label>
              <div data-testid="weekly-editor-revenue" className="w-full bg-brand-hover border border-brand-border rounded-md px-3 py-2.5 text-sm font-mono text-brand-ink flex items-center justify-between">
                <span>{fmtCurrency(dealsMrr)}</span>
                <span className="text-[10px] uppercase tracking-[0.12em] text-brand-tertiary">auto-summed from deals</span>
              </div>
            </div>
          </div>
          <CrmFillBanner info={crmInfo} loading={crmLoading} disabled={!weekDate} onFill={() => fillFromCrm(weekDate)} />
          <div>
            <p className="text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-2">Pipeline by Product (this week)</p>
            <div className="border border-brand-border rounded-md overflow-hidden">
              <div className="grid grid-cols-[1.6fr_1fr_1fr_1fr] bg-brand-hover text-[10px] uppercase tracking-[0.1em] text-brand-tertiary">
                <span className="px-3 py-2.5">Product</span>
                <span className="px-3 py-2.5 text-right border-l border-brand-border">Booked</span>
                <span className="px-3 py-2.5 text-right border-l border-brand-border">Completed</span>
                <span className="px-3 py-2.5 text-right border-l border-brand-border">Won</span>
              </div>
              {cats.map((c, i) => (
                <div key={c.name} className="grid grid-cols-[1.6fr_1fr_1fr_1fr] border-t border-brand-border items-center">
                  <span className="px-3 py-2 text-sm text-brand-ink">{c.name}</span>
                  {["tours_booked", "tours_completed", "won_deals"].map((f) => (
                    <input key={f} type="number" min="0" value={c[f]} onChange={(e) => setCat(i, f, e.target.value)} data-testid={`weekly-cat-${i}-${f}`}
                      className="w-full px-3 py-2 text-sm font-mono text-right text-brand-ink bg-transparent border-l border-brand-border focus:outline-none focus:bg-brand-hover" />
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div data-testid="weekly-closed-deals">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase tracking-[0.15em] text-brand-tertiary">
                Closed Deals — {location} <span className="text-brand-gold">· lifts the budget forecast</span>
              </p>
              {deals.length > 0 && <p className="text-[11px] font-mono text-brand-pos">+{fmtCurrency(dealsMrr)}/mo</p>}
            </div>
            {deals.length > 0 && (
              <div className="border border-brand-border rounded-md overflow-hidden mb-2">
                <div className="grid grid-cols-[1.5fr_1fr_1.2fr_0.9fr_0.8fr_36px] bg-brand-hover text-[10px] uppercase tracking-[0.1em] text-brand-tertiary">
                  <span className="px-3 py-2.5">Company</span>
                  <span className="px-3 py-2.5 border-l border-brand-border">Office / Suite #</span>
                  <span className="px-3 py-2.5 border-l border-brand-border">Product</span>
                  <span className="px-3 py-2.5 text-right border-l border-brand-border">Price / mo</span>
                  <span className="px-3 py-2.5 border-l border-brand-border">Term</span>
                  <span className="border-l border-brand-border" />
                </div>
                <OfficeOptions offices={offices} id="wk-office-list" />
                {deals.map((d, i) => (
                  <div key={i} className="grid grid-cols-[1.5fr_1fr_1.2fr_0.9fr_0.8fr_36px] border-t border-brand-border items-center">
                    <input value={d.company} onChange={(e) => setDeal(i, "company", e.target.value)} data-testid={`deal-${i}-company`} placeholder="Acme Inc."
                      className="w-full px-3 py-2 text-sm text-brand-ink bg-transparent focus:outline-none focus:bg-brand-hover" />
                    <input value={d.office} onChange={(e) => setDealOffice(i, e.target.value)} list="wk-office-list" data-testid={`deal-${i}-office`} placeholder="Search office #"
                      className="w-full px-3 py-2 text-sm text-brand-ink bg-transparent border-l border-brand-border focus:outline-none focus:bg-brand-hover" />
                    <select value={d.product} onChange={(e) => setDeal(i, "product", e.target.value)} data-testid={`deal-${i}-product`}
                      className="w-full px-3 py-2 text-sm text-brand-ink bg-transparent border-l border-brand-border focus:outline-none focus:bg-brand-hover">
                      <option value="">—</option>
                      {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <input type="number" min="0" value={d.monthly_price} onChange={(e) => setDeal(i, "monthly_price", e.target.value)} data-testid={`deal-${i}-price`} placeholder="0"
                      className="w-full px-3 py-2 text-sm font-mono text-right text-brand-ink bg-transparent border-l border-brand-border focus:outline-none focus:bg-brand-hover" />
                    <input value={d.term} onChange={(e) => setDeal(i, "term", e.target.value)} data-testid={`deal-${i}-term`} placeholder="12mo"
                      className="w-full px-3 py-2 text-sm text-brand-ink bg-transparent border-l border-brand-border focus:outline-none focus:bg-brand-hover" />
                    <button onClick={() => removeDeal(i)} data-testid={`deal-${i}-remove`} className="flex items-center justify-center h-full border-l border-brand-border text-brand-tertiary hover:text-brand-neg"><X className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            )}
            <button onClick={addDeal} data-testid="weekly-add-deal"
              className="flex items-center gap-1.5 text-sm text-brand-primary hover:text-brand-primaryHover">
              <Plus className="h-4 w-4" /> Add closed deal
            </button>
          </div>
          <div data-testid="weekly-notes-block">
            <label className="block text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-1.5">Notes <span className="text-brand-tertiary/70 normal-case tracking-normal">(optional)</span></label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="weekly-editor-notes" rows={3}
              placeholder="Context for this week — wins, blockers, tours worth flagging…"
              className="w-full bg-brand-page border border-brand-border rounded-md px-3 py-2.5 text-sm text-brand-ink focus:outline-none focus:border-brand-primary resize-y" />
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={onClose} className="px-5 py-2.5 text-sm text-brand-muted border border-brand-border hover:border-brand-borderStrong">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="weekly-editor-save"
              className="px-6 py-2.5 text-sm bg-brand-primary text-brand-page hover:bg-brand-primaryHover active:scale-[0.98] transition-all disabled:opacity-60">
              {saving ? "Saving…" : "Confirm & Save Week"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatBox({ label, value, tone }) {
  return (
    <div className="bg-brand-page border border-brand-border rounded-md px-4 py-3">
      <p className="text-[10px] uppercase tracking-[0.12em] text-brand-tertiary">{label}</p>
      <p className={`font-mono text-xl mt-1 ${tone || "text-brand-ink"}`}>{value}</p>
    </div>
  );
}

function WeekView({ week, onClose, onEdit }) {
  const cats = (week.categories || []).filter((c) => (c.tours_booked || c.tours_completed || c.won_deals));
  const deals = week.closed_deals || [];
  const tb = (week.categories || []).reduce((a, c) => a + (c.tours_booked || 0), 0);
  const tc = (week.categories || []).reduce((a, c) => a + (c.tours_completed || 0), 0);
  const wd = (week.categories || []).reduce((a, c) => a + (c.won_deals || 0), 0);
  const dealsMrr = deals.reduce((a, d) => a + (d.monthly_price || 0), 0);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-ink/40 backdrop-blur-sm" data-testid="week-view">
      <div className="bg-brand-surface border border-brand-borderStrong w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-brand-border sticky top-0 bg-brand-surface">
          <div>
            <p className="text-xs uppercase tracking-[0.15em] text-brand-tertiary">Week Summary</p>
            <p className="font-serif text-xl text-brand-ink">{week.location} · week of {fmtWeek(week.week_start)}</p>
            <p className="text-[11px] text-brand-tertiary mt-0.5">Rolls into {fmtPeriod(week.period)}</p>
          </div>
          <div className="flex items-center gap-1">
            {onEdit && <button onClick={onEdit} data-testid="week-view-edit" className="p-1.5 text-brand-tertiary hover:text-brand-primary"><Pencil className="h-4 w-4" /></button>}
            <button onClick={onClose} data-testid="week-view-close" className="p-1.5 text-brand-tertiary hover:text-brand-ink"><X className="h-5 w-5" /></button>
          </div>
        </div>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatBox label="Booked" value={tb} />
            <StatBox label="Completed" value={tc} />
            <StatBox label="Won" value={wd} tone="text-brand-pos" />
            <StatBox label="New Business" value={`${fmtCurrency(dealsMrr)}/mo`} tone="text-brand-pos" />
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-2">Pipeline by Product</p>
            {cats.length === 0 ? (
              <p className="text-sm text-brand-tertiary">No pipeline activity logged this week.</p>
            ) : (
              <div className="border border-brand-border rounded-md overflow-hidden">
                <div className="grid grid-cols-[1.6fr_1fr_1fr_1fr] bg-brand-hover text-[10px] uppercase tracking-[0.1em] text-brand-tertiary">
                  <span className="px-3 py-2.5">Product</span>
                  <span className="px-3 py-2.5 text-right border-l border-brand-border">Booked</span>
                  <span className="px-3 py-2.5 text-right border-l border-brand-border">Completed</span>
                  <span className="px-3 py-2.5 text-right border-l border-brand-border">Won</span>
                </div>
                {cats.map((c) => (
                  <div key={c.name} className="grid grid-cols-[1.6fr_1fr_1fr_1fr] border-t border-brand-border items-center">
                    <span className="px-3 py-2 text-sm text-brand-ink">{c.name}</span>
                    <span className="px-3 py-2 text-sm font-mono text-right text-brand-muted border-l border-brand-border">{c.tours_booked || 0}</span>
                    <span className="px-3 py-2 text-sm font-mono text-right text-brand-muted border-l border-brand-border">{c.tours_completed || 0}</span>
                    <span className="px-3 py-2 text-sm font-mono text-right text-brand-pos border-l border-brand-border">{c.won_deals || 0}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div data-testid="week-view-deals">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase tracking-[0.15em] text-brand-tertiary">Closed Deals</p>
              {deals.length > 0 && <p className="text-[11px] font-mono text-brand-pos">+{fmtCurrency(dealsMrr)}/mo</p>}
            </div>
            {deals.length === 0 ? (
              <p className="text-sm text-brand-tertiary">No deals closed this week.</p>
            ) : (
              <div className="border border-brand-border rounded-md overflow-hidden">
                <div className="grid grid-cols-[1.5fr_1fr_1.2fr_0.9fr_0.8fr] bg-brand-hover text-[10px] uppercase tracking-[0.1em] text-brand-tertiary">
                  <span className="px-3 py-2.5">Company</span>
                  <span className="px-3 py-2.5 border-l border-brand-border">Office / Suite #</span>
                  <span className="px-3 py-2.5 border-l border-brand-border">Product</span>
                  <span className="px-3 py-2.5 text-right border-l border-brand-border">Price / mo</span>
                  <span className="px-3 py-2.5 border-l border-brand-border">Term</span>
                </div>
                {deals.map((d, i) => (
                  <div key={i} className="grid grid-cols-[1.5fr_1fr_1.2fr_0.9fr_0.8fr] border-t border-brand-border items-center">
                    <span className="px-3 py-2 text-sm text-brand-ink">{d.company}</span>
                    <span className="px-3 py-2 text-sm text-brand-muted border-l border-brand-border">{d.office_number || "—"}</span>
                    <span className="px-3 py-2 text-sm text-brand-muted border-l border-brand-border">{d.product || "—"}</span>
                    <span className="px-3 py-2 text-sm font-mono text-right text-brand-ink border-l border-brand-border">{d.monthly_price ? fmtCurrency(d.monthly_price) : "—"}</span>
                    <span className="px-3 py-2 text-sm text-brand-muted border-l border-brand-border">{d.term || "—"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {week.notes && (
            <div data-testid="week-view-notes">
              <p className="text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-1.5">Notes</p>
              <p className="text-sm text-brand-muted leading-relaxed whitespace-pre-line bg-brand-page border border-brand-border rounded-md px-4 py-3">{week.notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function MoveOutEditor({ initial, isNew, locations, defaultLocation, onClose, onSaved }) {
  const [loc, setLoc] = useState(initial?.location || defaultLocation);
  const [company, setCompany] = useState(initial?.company || "");
  const [officeNumber, setOfficeNumber] = useState(initial?.office_number || "");
  const [desks, setDesks] = useState(initial?.desks ?? "");
  const [noticeDate, setNoticeDate] = useState(initial?.notice_date || "");
  const [vacateDate, setVacateDate] = useState(initial?.vacate_date || "");
  const [rent, setRent] = useState(initial?.monthly_rent ?? "");
  const [resold, setResold] = useState(initial?.resold ?? false);
  const [saving, setSaving] = useState(false);
  const offices = useOfficeCatalog(loc);
  const onOfficeChange = (v) => {
    setOfficeNumber(v);
    const m = offices.find((o) => o.office_number === v);
    if (m) {
      if (rent === "" || rent == null) setRent(m.monthly_price ?? "");
      if (desks === "" || desks == null) setDesks(m.desks ?? "");
    }
  };

  const save = async () => {
    if (!company.trim()) { toast.error("Enter a company name."); return; }
    if (!vacateDate) { toast.error("Enter the vacate date."); return; }
    if (!loc) { toast.error("Choose a location."); return; }
    setSaving(true);
    const body = { location: loc, company: company.trim(), office_number: officeNumber.trim() || null, desks: desks === "" ? null : parseInt(desks, 10) || 0, notice_date: noticeDate || null, vacate_date: vacateDate, monthly_rent: rent === "" ? 0 : parseFloat(rent), resold };
    try {
      if (isNew) await api.post("/sales/moveouts", body);
      else await api.put(`/sales/moveouts/${initial.id}`, body);
      toast.success(isNew ? "Notice added" : "Notice updated", { description: `${company} · ${loc}` });
      onSaved(); onClose();
    } catch (err) {
      toast.error("Save failed", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-ink/40 backdrop-blur-sm" data-testid="moveout-editor">
      <div className="bg-brand-surface border border-brand-borderStrong w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-brand-border">
          <p className="font-serif text-xl text-brand-ink">{isNew ? "Record a move-out notice" : "Edit move-out notice"}</p>
          <button onClick={onClose} data-testid="moveout-editor-close" className="p-1.5 text-brand-tertiary hover:text-brand-ink"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-1.5">Location</label>
              <select value={loc} onChange={(e) => setLoc(e.target.value)} data-testid="moveout-editor-location"
                className="w-full bg-brand-page border border-brand-border rounded-md px-3 py-2.5 text-sm text-brand-ink focus:outline-none focus:border-brand-primary">
                {locations.filter((l) => l !== "All Locations").map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-1.5">Office Number</label>
              <input value={officeNumber} onChange={(e) => onOfficeChange(e.target.value)} list="mo-office-list" data-testid="moveout-editor-office" placeholder="Search office #"
                className="w-full bg-brand-page border border-brand-border rounded-md px-3 py-2.5 text-sm text-brand-ink focus:outline-none focus:border-brand-primary" />
              <OfficeOptions offices={offices} id="mo-office-list" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-1.5">Company</label>
            <input value={company} onChange={(e) => setCompany(e.target.value)} data-testid="moveout-editor-company" placeholder="Acme Inc."
              className="w-full bg-brand-page border border-brand-border rounded-md px-3 py-2.5 text-sm text-brand-ink focus:outline-none focus:border-brand-primary" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-1.5">Notice Date</label>
              <input type="date" value={noticeDate} onChange={(e) => setNoticeDate(e.target.value)} data-testid="moveout-editor-notice"
                className="w-full bg-brand-page border border-brand-border rounded-md px-3 py-2.5 text-sm text-brand-ink focus:outline-none focus:border-brand-primary" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-1.5">Vacate Date</label>
              <input type="date" value={vacateDate} onChange={(e) => setVacateDate(e.target.value)} data-testid="moveout-editor-vacate"
                className="w-full bg-brand-page border border-brand-border rounded-md px-3 py-2.5 text-sm text-brand-ink focus:outline-none focus:border-brand-primary" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-1.5">Monthly Amount ($)</label>
              <input type="number" value={rent} onChange={(e) => setRent(e.target.value)} data-testid="moveout-editor-rent" placeholder="0"
                className="w-full bg-brand-page border border-brand-border rounded-md px-3 py-2.5 text-sm font-mono text-brand-ink focus:outline-none focus:border-brand-primary" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-1.5">Desks</label>
              <input type="number" min="0" value={desks} onChange={(e) => setDesks(e.target.value)} data-testid="moveout-editor-desks" placeholder="0"
                className="w-full bg-brand-page border border-brand-border rounded-md px-3 py-2.5 text-sm font-mono text-brand-ink focus:outline-none focus:border-brand-primary" />
            </div>
          </div>
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input type="checkbox" checked={resold} onChange={(e) => setResold(e.target.checked)} data-testid="moveout-editor-resold"
              className="h-4 w-4 accent-brand-primary cursor-pointer" />
            <span className="text-sm text-brand-ink">Space has been resold / backfilled</span>
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={onClose} className="px-5 py-2.5 text-sm text-brand-muted border border-brand-border hover:border-brand-borderStrong">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="moveout-editor-save"
              className="px-6 py-2.5 text-sm bg-brand-primary text-brand-page hover:bg-brand-primaryHover active:scale-[0.98] transition-all disabled:opacity-60">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function fmtDate(s) {
  if (!s) return "—";
  const d = new Date(s + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const REMOVAL_REASONS = [
  "No longer interested",
  "Upgraded / moved in",
  "Signed elsewhere",
  "Other",
];

function WaitlistRemoveModal({ entry, onClose, onConfirm }) {
  const [reason, setReason] = useState(REMOVAL_REASONS[0]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try { await onConfirm(reason, note.trim() || null); }
    finally { setSaving(false); }
  };
  const inputCls = "w-full bg-brand-page border border-brand-border rounded-md px-3 py-2.5 text-sm text-brand-ink focus:outline-none focus:border-brand-primary";
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-brand-ink/40 backdrop-blur-sm" data-testid="waitlist-remove-modal">
      <div className="bg-brand-surface border border-brand-borderStrong w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-brand-border">
          <div>
            <p className="text-xs uppercase tracking-[0.15em] text-brand-tertiary">Retire from waitlist</p>
            <p className="font-serif text-lg text-brand-ink">{entry?.name}</p>
          </div>
          <button onClick={onClose} data-testid="waitlist-remove-close" className="p-1.5 text-brand-tertiary hover:text-brand-ink"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-1.5">Reason</label>
            <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid="waitlist-remove-reason" className={inputCls}>
              {REMOVAL_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-1.5">Note {reason === "Other" ? "(required)" : "(optional)"}</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="waitlist-remove-note" rows={2} placeholder="Any extra context…" className={`${inputCls} resize-none`} />
          </div>
          <div className="flex justify-end gap-3 pt-1">
            <button onClick={onClose} className="px-5 py-2.5 text-sm text-brand-muted border border-brand-border hover:border-brand-borderStrong">Cancel</button>
            <button onClick={submit} disabled={saving || (reason === "Other" && !note.trim())} data-testid="waitlist-remove-confirm"
              className="px-6 py-2.5 text-sm bg-brand-neg text-brand-page hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-60">
              {saving ? "Retiring…" : "Retire"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function WaitlistEditor({ initial, isNew, kind, location, onClose, onSaved }) {
  const isMember = kind === "member";
  const [name, setName] = useState(initial?.name || "");
  const [currentSpace, setCurrentSpace] = useState(initial?.current_space || "");
  const [direction, setDirection] = useState(initial?.direction || "upgrade");
  const [desiredSpace, setDesiredSpace] = useState(initial?.desired_space || "");
  const [dateExpressed, setDateExpressed] = useState(initial?.date_expressed || "");
  const [targetDate, setTargetDate] = useState(initial?.target_date || "");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast.error(isMember ? "Enter a member or company." : "Enter a company or prospect."); return; }
    setSaving(true);
    const body = {
      kind, location, name: name.trim(),
      current_space: isMember ? (currentSpace.trim() || null) : null,
      direction: isMember ? direction : null,
      desired_space: desiredSpace.trim() || null,
      date_expressed: dateExpressed || null,
      target_date: targetDate || null,
      notes: notes.trim() || null,
    };
    try {
      if (isNew) await api.post("/waitlist", body);
      else await api.put(`/waitlist/${initial.id}`, body);
      toast.success(isNew ? "Added to waitlist" : "Waitlist updated", { description: `${name.trim()} · ${location}` });
      onSaved(); onClose();
    } catch (err) {
      toast.error("Save failed", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setSaving(false); }
  };

  const inputCls = "w-full bg-brand-page border border-brand-border rounded-md px-3 py-2.5 text-sm text-brand-ink focus:outline-none focus:border-brand-primary";
  const labelCls = "block text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-1.5";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-ink/40 backdrop-blur-sm" data-testid={`waitlist-editor-${kind}`}>
      <div className="bg-brand-surface border border-brand-borderStrong w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-brand-border sticky top-0 bg-brand-surface">
          <div>
            <p className="text-xs uppercase tracking-[0.15em] text-brand-tertiary">{isNew ? "Add to" : "Edit"} {isMember ? "member" : "tour"} waitlist</p>
            <p className="font-serif text-xl text-brand-ink">{location}</p>
          </div>
          <button onClick={onClose} data-testid="waitlist-editor-close" className="p-1.5 text-brand-tertiary hover:text-brand-ink"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className={labelCls}>{isMember ? "Member / Company" : "Company / Prospect"}</label>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="waitlist-editor-name" placeholder={isMember ? "Acme Inc." : "Prospect name"} className={inputCls} />
          </div>
          {isMember && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Current Office / Plan</label>
                <input value={currentSpace} onChange={(e) => setCurrentSpace(e.target.value)} data-testid="waitlist-editor-current" placeholder="Office 12 · 4-seat" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Direction</label>
                <select value={direction} onChange={(e) => setDirection(e.target.value)} data-testid="waitlist-editor-direction" className={inputCls}>
                  <option value="upgrade">Upgrade</option>
                  <option value="downgrade">Downgrade</option>
                </select>
              </div>
            </div>
          )}
          <div>
            <label className={labelCls}>{isMember ? "Desired Office / Plan" : "Desired Office / Space Type"}</label>
            <input value={desiredSpace} onChange={(e) => setDesiredSpace(e.target.value)} data-testid="waitlist-editor-desired" placeholder={isMember ? "Office 20 · 8-seat" : "Private office · 6-seat"} className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Date Interest Expressed</label>
              <input type="date" value={dateExpressed} onChange={(e) => setDateExpressed(e.target.value)} data-testid="waitlist-editor-date-expressed" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>{isMember ? "Target Move Date" : "Target Move-In Date"}</label>
              <input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} data-testid="waitlist-editor-target-date" className={inputCls} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="waitlist-editor-notes" rows={3} placeholder="Any other relevant details…"
              className={`${inputCls} resize-none`} />
          </div>
          <div className="flex justify-end gap-3 pt-1">
            <button onClick={onClose} className="px-5 py-2.5 text-sm text-brand-muted border border-brand-border hover:border-brand-borderStrong">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="waitlist-editor-save"
              className="px-6 py-2.5 text-sm bg-brand-primary text-brand-page hover:bg-brand-primaryHover active:scale-[0.98] transition-all disabled:opacity-60">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function WaitlistSection({ kind, entries, location, specificLoc, onAdd, onEdit, onRemove, onRestore, onHardDelete }) {
  const [showRemoved, setShowRemoved] = useState(false);
  const isMember = kind === "member";
  const Icon = isMember ? Users : UserPlus;
  const title = isMember ? "Member Waitlist" : "Tour Waitlist";
  const subtitle = isMember
    ? "Members interested in upgrading or downgrading their space"
    : "Tours who wanted an office that wasn't available";
  const active = entries.filter((w) => w.status !== "removed");
  const removed = entries.filter((w) => w.status === "removed");
  const cols = isMember
    ? ["Member / Company", "Current", "Change", "Desired", "Expressed", "Target Move", "Notes", ""]
    : ["Company / Prospect", "Desired Office", "Expressed", "Target Move-In", "Notes", ""];
  const testid = isMember ? "member-waitlist-section" : "tour-waitlist-section";

  return (
    <div className="bg-brand-surface border border-brand-border" data-testid={testid}>
      <div className="px-6 py-4 border-b border-brand-border flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-brand-primary" />
          <div>
            <p className="text-xs uppercase tracking-[0.15em] text-brand-tertiary">{title} <span className="text-brand-gold">· staff only</span></p>
            <p className="font-serif text-lg text-brand-ink">{subtitle}</p>
          </div>
        </div>
        {specificLoc ? (
          <button onClick={onAdd} data-testid={`${kind}-waitlist-add`}
            className="flex items-center gap-2 bg-brand-primary text-brand-page px-4 py-2 text-sm hover:bg-brand-primaryHover active:scale-[0.98] transition-all">
            <Plus className="h-4 w-4" /> Add
          </button>
        ) : (
          <p className="text-[11px] text-brand-tertiary">Pick a single location to manage this list</p>
        )}
      </div>
      {specificLoc && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-brand-tertiary border-b border-brand-border">
                  {cols.map((h, i) => (
                    <th key={h || "wl-act"} className={`font-medium uppercase tracking-[0.1em] text-[11px] px-4 py-3 ${i === 0 ? "text-left px-6" : "text-left"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {active.length === 0 && (<tr><td colSpan={cols.length} className="px-6 py-8 text-center text-brand-tertiary">No one on this list yet for {location}.</td></tr>)}
                {active.map((w) => (
                  <tr key={w.id} data-testid={`${kind}-waitlist-row-${w.id}`} className="border-b border-brand-border last:border-0 hover:bg-brand-hover align-top">
                    <td className="px-6 py-3 text-brand-ink">{w.name}</td>
                    {isMember && <td className="px-4 py-3 text-brand-muted">{w.current_space || "—"}</td>}
                    {isMember && (
                      <td className="px-4 py-3">
                        {w.direction ? (
                          <span className={`text-[11px] uppercase tracking-[0.08em] font-medium ${w.direction === "upgrade" ? "text-brand-pos" : "text-brand-gold"}`}>{w.direction}</span>
                        ) : "—"}
                      </td>
                    )}
                    <td className="px-4 py-3 text-brand-muted">{w.desired_space || "—"}</td>
                    <td className="px-4 py-3 text-brand-muted whitespace-nowrap">{fmtDate(w.date_expressed)}</td>
                    <td className="px-4 py-3 text-brand-muted whitespace-nowrap">{fmtDate(w.target_date)}</td>
                    <td className="px-4 py-3 text-brand-tertiary max-w-[220px]">{w.notes || "—"}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => onEdit(w)} data-testid={`${kind}-waitlist-edit-${w.id}`} className="p-1.5 text-brand-tertiary hover:text-brand-primary"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => onRemove(w)} data-testid={`${kind}-waitlist-delete-${w.id}`} title="Retire (mark inactive with a reason)" className="ml-1 inline-flex items-center gap-1 text-xs border border-brand-border rounded-full px-2.5 py-1 text-brand-muted hover:text-brand-ink hover:border-brand-ink transition-colors"><Archive className="h-3.5 w-3.5" /> Retire</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {removed.length > 0 && (
            <div className="border-t border-brand-border">
              <button onClick={() => setShowRemoved((s) => !s)} data-testid={`${kind}-waitlist-toggle-removed`}
                className="w-full px-6 py-2.5 flex items-center gap-2 text-[11px] uppercase tracking-[0.12em] text-brand-tertiary hover:text-brand-ink transition-colors">
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showRemoved ? "rotate-180" : ""}`} />
                {showRemoved ? "Hide" : "Show"} inactive / retired ({removed.length})
              </button>
              {showRemoved && (
                <div className="overflow-x-auto bg-brand-hover/40">
                  <table className="w-full text-sm">
                    <tbody>
                      {removed.map((w) => (
                        <tr key={w.id} data-testid={`${kind}-waitlist-removed-${w.id}`} className="border-t border-brand-border align-top">
                          <td className="px-6 py-3 text-brand-muted line-through">{w.name}</td>
                          <td className="px-4 py-3 text-brand-muted">{w.desired_space || "—"}</td>
                          <td className="px-4 py-3">
                            <span className="text-[11px] uppercase tracking-[0.08em] text-brand-neg font-medium">{w.removal_reason || "Removed"}</span>
                            {w.removal_note && <span className="block text-[11px] text-brand-tertiary mt-0.5">{w.removal_note}</span>}
                          </td>
                          <td className="px-4 py-3 text-brand-tertiary whitespace-nowrap text-[11px]">{fmtDate(w.removed_at ? w.removed_at.slice(0, 10) : null)}</td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <button onClick={() => onRestore(w)} data-testid={`${kind}-waitlist-restore-${w.id}`} className="p-1.5 text-brand-tertiary hover:text-brand-pos" title="Restore"><RotateCcw className="h-4 w-4" /></button>
                            <button onClick={() => onHardDelete(w)} data-testid={`${kind}-waitlist-purge-${w.id}`} className="p-1.5 text-brand-tertiary hover:text-brand-neg" title="Delete permanently"><Trash2 className="h-4 w-4" /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function Sales() {
  const { user } = useAuth();
  const canEdit = ["admin", "regional", "community"].includes(user?.role);
  const [meta, setMeta] = useState(null);
  const [location, setLocation] = useState(() => new URLSearchParams(window.location.search).get("location") || "All Locations");
  const [period, setPeriod] = useState("");
  const [product, setProduct] = useState("all");
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [weeks, setWeeks] = useState([]);
  const [earlierWeeks, setEarlierWeeks] = useState([]);
  const [dealsData, setDealsData] = useState({ recent: [], rollup: [], totals: { count: 0, mrr: 0 }, recap: { this_count: 0, this_mrr: 0, last_mrr: 0 } });
  const [moData, setMoData] = useState({ upcoming: [], recent_vacated: [], rollup: [], vacated_totals: { count: 0, mrr: 0, resold: 0 } });
  const [editingWeek, setEditingWeek] = useState(null);
  const [viewingWeek, setViewingWeek] = useState(null);
  const [showEarlierWeeks, setShowEarlierWeeks] = useState(false);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  const [showChurn, setShowChurn] = useState(false);
  const [editingMoveOut, setEditingMoveOut] = useState(null);
  const [memberWaitlist, setMemberWaitlist] = useState([]);
  const [tourWaitlist, setTourWaitlist] = useState([]);
  const [editingWaitlist, setEditingWaitlist] = useState(null);
  const [removingWaitlist, setRemovingWaitlist] = useState(null);

  const loadMeta = useCallback(async () => {
    try {
      const { data } = await api.get("/sales/meta");
      setMeta(data);
      setPeriod((prev) => prev || (data.periods?.length ? `${data.periods[0]}|${data.latest_period}` : ""));
    } catch { /* keep prior meta */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/sales", { params: { location } });
      setData(data);
    } catch { /* keep prior data */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const monthWindow = () => {
    const cm = new Date().toISOString().slice(0, 7);
    return { current: cm, prior: prevMonth(cm), today: new Date().toISOString().slice(0, 10) };
  };

  const loadWeekly = useCallback(async () => {
    setShowEarlierWeeks(false); setEarlierWeeks([]);
    if (!canEdit || location === "All Locations") { setWeeks([]); return; }
    try {
      const { data } = await api.get("/sales/weekly", { params: { location, since: monthWindow().current } });
      setWeeks(data.weeks || []);
    } catch (e) { setWeeks([]); toast.error("Weekly entries couldn't load", { description: e.userMessage, id: "weeks" }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit, location]);

  const toggleEarlierWeeks = useCallback(async () => {
    if (showEarlierWeeks) { setShowEarlierWeeks(false); return; }
    setLoadingEarlier(true);
    try {
      const { data } = await api.get("/sales/weekly", { params: { location, before: monthWindow().current } });
      setEarlierWeeks(data.weeks || []); setShowEarlierWeeks(true);
    } catch { setEarlierWeeks([]); }
    finally { setLoadingEarlier(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, showEarlierWeeks]);

  const loadDeals = useCallback(async () => {
    if (!canEdit || location === "All Locations") {
      setDealsData({ recent: [], rollup: [], totals: { count: 0, mrr: 0 }, recap: { this_count: 0, this_mrr: 0, last_mrr: 0 } });
      return;
    }
    try {
      const { current, prior } = monthWindow();
      const { data } = await api.get("/sales/deals/summary", { params: { location, current, prior } });
      setDealsData(data);
    } catch { /* keep last */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit, location]);

  const loadMoveOuts = useCallback(async () => {
    if (!canEdit) { setMoData({ upcoming: [], recent_vacated: [], rollup: [], vacated_totals: { count: 0, mrr: 0, resold: 0 } }); return; }
    try {
      const { current, prior, today } = monthWindow();
      const { data } = await api.get("/sales/moveouts/summary", { params: { location, current, prior, today } });
      setMoData(data);
    } catch { /* keep last */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit, location]);

  const loadDealDetail = useCallback((month) =>
    api.get("/sales/deals", { params: { location, month } }).then((r) => r.data.deals), [location]);
  const loadMoDetail = useCallback((month) =>
    api.get("/sales/moveouts/month", { params: { location, month } }).then((r) => r.data.move_outs), [location]);

  const loadWaitlist = useCallback(async () => {
    if (!canEdit || location === "All Locations") { setMemberWaitlist([]); setTourWaitlist([]); return; }
    try {
      const { data } = await api.get("/waitlist", { params: { location } });
      const list = data.waitlist || [];
      setMemberWaitlist(list.filter((w) => w.kind === "member"));
      setTourWaitlist(list.filter((w) => w.kind === "tour"));
    } catch (e) { setMemberWaitlist([]); setTourWaitlist([]); toast.error("Waitlists couldn't load", { description: e.userMessage, id: "waitlists" }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit, location]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { load(); }, [load]);

  // Apply the user's saved default date-range preference once meta + auth are ready.
  // Sales defaults to "all" when no preference is set (full monthly roll-up).
  const prefApplied = useRef(false);
  useEffect(() => {
    if (prefApplied.current || !meta?.periods?.length || user === null) return;
    prefApplied.current = true;
    const pref = (user && user.preferences && user.preferences.default_period_range) || "all";
    setPeriod(pref === "latest"
      ? `${meta.latest_period}|${meta.latest_period}`
      : `${meta.periods[0]}|${meta.latest_period}`);
  }, [meta, user]);
  useEffect(() => { loadWeekly(); }, [loadWeekly]);
  useEffect(() => { loadDeals(); }, [loadDeals]);
  useEffect(() => { loadMoveOuts(); }, [loadMoveOuts]);
  useEffect(() => { loadWaitlist(); }, [loadWaitlist]);

  if (!meta || !data) {
    return <div className="min-h-screen bg-brand-page flex items-center justify-center relative z-10"><div className="h-10 w-10 rounded-full border-2 border-brand-border border-t-brand-primary animate-spin" /></div>;
  }

  const [start, end] = period ? period.split("|") : [null, null];
  const inRange = (m) => (!start || m.period >= start) && (!end || m.period <= end);
  const scopeProduct = (months) => {
    if (product === "all") return months;
    return months.map((m) => {
      const c = (m.categories || []).find((x) => x.name === product) || {};
      const tb = c.tours_booked || 0, tc = c.tours_completed || 0, wd = c.won_deals || 0;
      return { ...m, tours_booked: tb, tours_completed: tc, won_deals: wd,
        lead_to_deal_ratio: tb ? tc / tb : null, close_ratio: tb ? wd / tb : null };
    });
  };
  const allMonths = data.months;
  const scoped = scopeProduct(allMonths);
  const filtered = scoped.filter(inRange);
  const s = summarize(filtered);
  const catRows = summarizeCategories(allMonths.filter(inRange));
  const chart = scoped.map((m) => ({ period: m.period, booked: m.tours_booked, completed: m.tours_completed, won: m.won_deals }));
  const editable = data.editable;
  const periodOptions = buildPeriodOptions(meta.periods, meta.latest_period);
  const locationOptions = meta.locations.map((l) => ({ value: l, label: l }));
  const productOptions = [{ value: "all", label: "All Products" }, ...(meta.categories || []).map((c) => ({ value: c, label: c }))];
  const defaultEditLocation = editable ? location : meta.locations.find((l) => l !== "All Locations");

  const remove = async (m) => {
    if (!window.confirm(`Delete ${m.location} sales for ${fmtPeriod(m.period)}?`)) return;
    try {
      await api.delete(`/sales/${m.period}`, { params: { location: m.location } });
      toast.success("Deleted"); load();
    } catch (err) { toast.error("Delete failed", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
  };

  const removeWeek = async (w) => {
    if (!window.confirm(`Delete week of ${fmtWeek(w.week_start)} for ${w.location}?`)) return;
    try { await api.delete(`/sales/weekly/${w.id}`); toast.success("Week deleted"); loadWeekly(); loadDeals(); load(); }
    catch (err) { toast.error("Delete failed", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
  };

  const removeMoveOut = async (mo) => {
    if (!window.confirm(`Delete move-out notice for ${mo.company}?`)) return;
    try { await api.delete(`/sales/moveouts/${mo.id}`); toast.success("Deleted"); loadMoveOuts(); load(); }
    catch (err) { toast.error("Delete failed", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
  };

  const toggleResold = async (m) => {
    try {
      await api.put(`/sales/moveouts/${m.id}`, {
        location: m.location, company: m.company, office_number: m.office_number || null,
        desks: m.desks ?? null, notice_date: m.notice_date || null, vacate_date: m.vacate_date,
        monthly_rent: m.monthly_rent, resold: !m.resold,
      });
      loadMoveOuts();
    } catch (err) { toast.error("Update failed", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
  };

  const openRemoveWaitlist = (w) => setRemovingWaitlist(w);
  const confirmRemoveWaitlist = async (reason, note) => {
    try {
      await api.post(`/waitlist/${removingWaitlist.id}/remove`, { reason, note });
      toast.success("Retired from waitlist", { description: `${removingWaitlist.name} · ${reason}` });
      setRemovingWaitlist(null); loadWaitlist();
    } catch (err) { toast.error("Remove failed", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
  };
  const restoreWaitlist = async (w) => {
    try { await api.post(`/waitlist/${w.id}/restore`); toast.success("Restored to waitlist"); loadWaitlist(); }
    catch (err) { toast.error("Restore failed", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
  };
  const purgeWaitlist = async (w) => {
    if (!window.confirm(`Permanently delete ${w.name} from history?`)) return;
    try { await api.delete(`/waitlist/${w.id}`); toast.success("Deleted"); loadWaitlist(); }
    catch (err) { toast.error("Delete failed", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
  };

  const specificLoc = location !== "All Locations";
  const mo = data.move_out_summary || { count: 0, monthly_at_risk: 0 };
  const todayStr = new Date().toISOString().slice(0, 10);
  const currentMonthStr = todayStr.slice(0, 7);
  const priorMonthStr = prevMonth(currentMonthStr);
  // Weekly management table: this month is loaded by default; earlier weeks paged on demand.
  const shownWeeks = showEarlierWeeks ? [...weeks, ...earlierWeeks] : weeks;

  // Closed-deal pipeline (server-paged): recent detail + older-month roll-up aggregates.
  const recentDeals = dealsData.recent || [];
  const olderDealMonths = (dealsData.rollup || []).map((r) => ({ key: r.month, ...r }));
  const dealsTotals = dealsData.totals || { count: 0, mrr: 0 };
  // Move-outs (server-paged).
  const upcomingMoveOuts = moData.upcoming || [];
  const recentVacated = moData.recent_vacated || [];
  const olderVacatedMonths = (moData.rollup || []).map((r) => ({ key: r.month, ...r }));
  const vacatedTotals = moData.vacated_totals || { count: 0, mrr: 0, resold: 0 };
  // Overview recap (this month vs last month), all computed server-side except this-month churn.
  const bizThisMrr = dealsData.recap?.this_mrr || 0;
  const bizThisCount = dealsData.recap?.this_count || 0;
  const bizLastMrr = dealsData.recap?.last_mrr || 0;
  const churnThisMrr = recentVacated
    .filter((m) => (m.vacate_date || "").slice(0, 7) === currentMonthStr)
    .reduce((a, m) => a + (m.monthly_rent || 0), 0);

  // Backfill matches: active waitlist entries at the same location whose desired
  // space fits the vacating office (or who are open to any space).
  const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  const activeWaitlist = [...memberWaitlist, ...tourWaitlist].filter((w) => w.status !== "removed");
  const matchesFor = (m) => {
    const office = norm(m.office_number);
    return activeWaitlist.filter((w) => {
      if (w.location !== m.location) return false;
      const desired = norm(w.desired_space);
      if (!office || !desired) return true;
      return desired.includes(office) || office.includes(desired);
    });
  };

  return (
    <div className="min-h-screen bg-brand-page relative z-10">
      <Header />

      <main className="max-w-[1600px] mx-auto px-6 lg:px-12 py-10 space-y-8">
        <AskBar />
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-brand-tertiary">Sales Pipeline · {location} · {product === "all" ? "All Products" : product} · {start === end ? fmtPeriod(end) : `${fmtPeriod(start)} – ${fmtPeriod(end)}`}</p>
            <h1 className="font-serif text-4xl lg:text-5xl font-light tracking-tight text-brand-ink mt-2">Sales &amp; Leasing</h1>
            <p className="text-sm text-brand-muted mt-1">Tours, deals and closings. Editable directly on this page.</p>
          </div>
          <div className="flex flex-wrap items-end gap-4">
            <Select label="Location" testid="sales-filter-location" value={location} onChange={setLocation} options={locationOptions} />
            <Select label="Product Type" testid="sales-filter-product" value={product} onChange={setProduct} options={productOptions} />
            <Select label="Time Period" testid="sales-filter-period" value={period} onChange={setPeriod} options={periodOptions} />
            <div className="pb-2"><DefaultViewSelect /></div>
            {canEdit && (
              <button onClick={() => setEditing({ month: null, isNew: true })} data-testid="sales-add-month"
                className="flex items-center gap-2 bg-brand-primary text-brand-page px-5 py-2.5 text-sm hover:bg-brand-primaryHover active:scale-[0.98] transition-all">
                <Plus className="h-4 w-4" /> Add Month
              </button>
            )}
          </div>
        </div>

        {canEdit && <SalesAvailability location={location} onSelectLocation={setLocation} />}

        <div className="flex items-center gap-1 border-b border-brand-border" data-testid="sales-tabs">
          <button onClick={() => setTab("overview")} data-testid="sales-tab-overview"
            className={`px-4 py-2.5 text-sm -mb-px border-b-2 transition-colors ${tab === "overview" ? "border-brand-primary text-brand-ink font-medium" : "border-transparent text-brand-muted hover:text-brand-ink"}`}>
            Overview
          </button>
          {canEdit && (
            <button onClick={() => setTab("tools")} data-testid="sales-tab-tools"
              className={`px-4 py-2.5 text-sm -mb-px border-b-2 transition-colors ${tab === "tools" ? "border-brand-primary text-brand-ink font-medium" : "border-transparent text-brand-muted hover:text-brand-ink"}`}>
              Working Tools
            </button>
          )}
        </div>

        {tab === "overview" && (<>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-brand-border border border-brand-border">
          <Kpi index={0} label="Tours Booked" value={s.tb} sub={`${filtered.length} months`} />
          <Kpi index={1} label="Tours Completed" value={s.tc} sub={fmtPct(s.lead) + " of booked"} />
          <Kpi index={2} label="Won Deals" value={s.wd} highlight sub="closings" />
          <Kpi index={3} label="Close Ratio" value={fmtPct(s.close)} sub="won / booked" />
          <Kpi index={4} label="Actual Revenue" value={fmtCurrency(s.rev)} sub={product === "all" ? "recorded" : "location total"} />
        </div>

        {canEdit && specificLoc && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-brand-border border border-brand-border" data-testid="sales-recap">
            <RecapCard index={0} testid="recap-new-business" tone="text-brand-pos"
              label={`New Business · ${fmtPeriod(currentMonthStr)}`} value={`${fmtCurrency(bizThisMrr)}/mo`}
              sub={`${bizThisCount} ${bizThisCount === 1 ? "deal" : "deals"} closed this month`} delta={bizThisMrr - bizLastMrr} />
            <RecapCard index={1} testid="recap-moveouts" tone="text-brand-neg"
              label="Move-Outs · upcoming" value={`${mo.count}`}
              sub={`${fmtCurrency(mo.monthly_at_risk)}/mo at risk`} />
            <RecapCard index={2} testid="recap-net-mrr" tone={bizThisMrr - churnThisMrr >= 0 ? "text-brand-pos" : "text-brand-neg"}
              label={`Net New MRR · ${fmtPeriod(currentMonthStr)}`} value={`${fmtCurrency(bizThisMrr - churnThisMrr)}/mo`}
              sub="new business − churn this month" />
          </div>
        )}

        <div className="bg-brand-surface border border-brand-border p-6">
          <div className="flex items-baseline justify-between mb-6">
            <div>
              <p className="text-xs uppercase tracking-[0.15em] text-brand-tertiary">Pipeline Trend</p>
              <p className="font-serif text-xl text-brand-ink mt-1">Tours &amp; closings by month</p>
            </div>
            <div className="hidden sm:flex items-center gap-4 text-xs text-brand-muted">
              <span className="flex items-center gap-1.5"><span className="h-2 w-3 bg-brand-gold" /> Booked</span>
              <span className="flex items-center gap-1.5"><span className="h-2 w-3 bg-brand-tertiary" /> Completed</span>
              <span className="flex items-center gap-1.5"><span className="h-2 w-3 bg-brand-primary" /> Won</span>
            </div>
          </div>
          {chart.length === 0 ? (
            <p className="py-16 text-center text-brand-tertiary text-sm">No sales data for {location} yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={chart} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="#E8E3DB" />
                <XAxis dataKey="period" tickFormatter={fmtPeriodShort} tick={{ fill: "#8A847A", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#8A847A", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<TrendTooltip />} cursor={{ fill: "rgba(30,58,47,0.04)" }} />
                <Bar dataKey="booked" name="Booked" fill="#C5A880" barSize={14} radius={[2, 2, 0, 0]} />
                <Bar dataKey="completed" name="Completed" fill="#8A847A" barSize={14} radius={[2, 2, 0, 0]} />
                <Line dataKey="won" name="Won" stroke="#1E3A2F" strokeWidth={2} dot={{ r: 3, fill: "#1E3A2F" }} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-brand-surface border border-brand-border" data-testid="sales-type-breakdown">
          <div className="px-6 py-4 border-b border-brand-border flex items-baseline justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.15em] text-brand-tertiary">Breakdown by Product Type</p>
              <p className="font-serif text-xl text-brand-ink mt-1">Tours &amp; closings by product</p>
            </div>
            <p className="text-[11px] text-brand-tertiary hidden sm:block">{location} · {start === end ? fmtPeriod(end) : `${fmtPeriod(start)} – ${fmtPeriod(end)}`}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-brand-tertiary border-b border-brand-border">
                  {["Product Type", "Booked", "Completed", "Won", "Close"].map((h, i) => (
                    <th key={h} className={`font-medium uppercase tracking-[0.1em] text-[11px] px-4 py-3 ${i === 0 ? "text-left px-6" : "text-right"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {catRows.map((c) => (
                  <tr key={c.name} data-testid={`sales-type-${c.name.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                    className="border-b border-brand-border last:border-0 hover:bg-brand-hover">
                    <td className="px-6 py-3.5 text-brand-ink">{c.name}</td>
                    <td className="px-4 py-3.5 text-right font-mono text-brand-muted">{c.booked}</td>
                    <td className="px-4 py-3.5 text-right font-mono text-brand-muted">{c.completed}</td>
                    <td className="px-4 py-3.5 text-right font-mono text-brand-pos font-medium">{c.won}</td>
                    <td className="px-4 py-3.5 text-right font-mono text-brand-muted">{fmtPct(c.booked ? c.won / c.booked : null)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-brand-borderStrong bg-brand-hover font-medium" data-testid="sales-type-total">
                  <td className="px-6 py-3.5 text-brand-ink">Total</td>
                  <td className="px-4 py-3.5 text-right font-mono text-brand-ink">{s.tb}</td>
                  <td className="px-4 py-3.5 text-right font-mono text-brand-ink">{s.tc}</td>
                  <td className="px-4 py-3.5 text-right font-mono text-brand-pos">{s.wd}</td>
                  <td className="px-4 py-3.5 text-right font-mono text-brand-ink">{fmtPct(s.close)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-brand-surface border border-brand-border" data-testid="sales-table">
          <div className="px-6 py-4 border-b border-brand-border flex items-center justify-between">
            <p className="text-xs uppercase tracking-[0.15em] text-brand-tertiary">Monthly Detail</p>
            {!editable && <p className="text-[11px] text-brand-tertiary">Select a single location to edit rows</p>}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-brand-tertiary border-b border-brand-border">
                  {["Month", "Booked", "Completed", "Won", "Lead→Deal", "Close", "Actual Rev.", ""].map((h, i) => (
                    <th key={h || "actions"} className={`font-medium uppercase tracking-[0.1em] text-[11px] px-4 py-3 ${i === 0 ? "text-left px-6" : "text-right"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (<tr><td colSpan={8} className="px-6 py-8 text-center text-brand-tertiary">No sales data for this selection.</td></tr>)}
                {[...filtered].reverse().map((m) => (
                  <tr key={m.period} data-testid={`sales-row-${m.period}`} className="border-b border-brand-border last:border-0 hover:bg-brand-hover group">
                    <td className="px-6 py-3.5 text-brand-ink">{fmtPeriod(m.period)}</td>
                    <td className="px-4 py-3.5 text-right font-mono text-brand-muted">{m.tours_booked}</td>
                    <td className="px-4 py-3.5 text-right font-mono text-brand-muted">{m.tours_completed}</td>
                    <td className="px-4 py-3.5 text-right font-mono text-brand-pos font-medium">{m.won_deals}</td>
                    <td className="px-4 py-3.5 text-right font-mono text-brand-muted">{fmtPct(m.lead_to_deal_ratio)}</td>
                    <td className="px-4 py-3.5 text-right font-mono text-brand-muted">{fmtPct(m.close_ratio)}</td>
                    <td className="px-4 py-3.5 text-right font-mono text-brand-ink">{m.actual_revenue ? fmtCurrency(m.actual_revenue) : "—"}</td>
                    <td className="px-4 py-3.5 text-right whitespace-nowrap">
                      {editable && (
                        <>
                          <button onClick={() => setEditing({ month: m, isNew: false })} data-testid={`sales-edit-${m.period}`} className="p-1.5 text-brand-tertiary hover:text-brand-primary transition-colors"><Pencil className="h-4 w-4" /></button>
                          <button onClick={() => remove(m)} data-testid={`sales-delete-${m.period}`} className="p-1.5 text-brand-tertiary hover:text-brand-neg transition-colors"><Trash2 className="h-4 w-4" /></button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Weekly entry — regional/admin only, never shown to investors */}
        </>)}

        {tab === "tools" && (<>
        {canEdit && (
          <div className="bg-brand-surface border border-brand-border" data-testid="weekly-section">
            <div className="px-6 py-4 border-b border-brand-border flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <CalendarClock className="h-4 w-4 text-brand-primary" />
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-brand-tertiary">Weekly Entry <span className="text-brand-gold">· staff only</span></p>
                  <p className="font-serif text-lg text-brand-ink">{fmtPeriod(currentMonthStr)} — this month's weeks</p>
                  <p className="text-[11px] text-brand-tertiary mt-0.5">Mon–Fri numbers roll up into the month; completed months move to Monthly Detail.</p>
                </div>
              </div>
              {specificLoc ? (
                <button onClick={() => setEditingWeek({ week: null, isNew: true })} data-testid="weekly-add"
                  className="flex items-center gap-2 bg-brand-primary text-brand-page px-4 py-2 text-sm hover:bg-brand-primaryHover active:scale-[0.98] transition-all">
                  <Plus className="h-4 w-4" /> Add Week
                </button>
              ) : (
                <p className="text-[11px] text-brand-tertiary">Pick a single location to add weekly entries</p>
              )}
            </div>
            {specificLoc && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-brand-tertiary border-b border-brand-border">
                      {["Week", "Rolls Into", "Booked", "Completed", "Won", "Closed Deals", "New Business $", ""].map((h, i) => (
                        <th key={h || "wk-act"} className={`font-medium uppercase tracking-[0.1em] text-[11px] px-4 py-3 ${i === 0 ? "text-left px-6" : "text-right"}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pendingWeeks(weeks, todayStr).map((wk) => (
                      <tr key={`pending-${wk}`} data-testid={`weekly-pending-${wk}`} className="border-b border-brand-border bg-brand-gold/5">
                        <td className="px-6 py-3 text-brand-ink">{fmtWeek(wk)}<p className="text-[10px] uppercase tracking-[0.12em] text-brand-gold mt-0.5">Not confirmed yet</p></td>
                        <td colSpan={6} className="px-4 py-3 text-right text-xs text-brand-muted">CRM numbers are ready to review</td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => setEditingWeek({ week: { week_start: wk }, isNew: true })} data-testid={`weekly-review-${wk}`}
                            className="text-xs bg-brand-primary text-brand-page px-3 py-1.5 hover:bg-brand-primaryHover transition-colors whitespace-nowrap">Review & confirm</button>
                        </td>
                      </tr>
                    ))}
                    {shownWeeks.length === 0 && pendingWeeks(weeks, todayStr).length === 0 && (<tr><td colSpan={8} className="px-6 py-8 text-center text-brand-tertiary">{weeks.length === 0 ? `No weekly entries yet for ${location}.` : `No weekly entries for ${fmtPeriod(currentMonthStr)}. Earlier weeks are rolled into Monthly Detail above.`}</td></tr>)}
                    {shownWeeks.map((w) => {
                      const tb = (w.categories || []).reduce((a, c) => a + (c.tours_booked || 0), 0);
                      const tc = (w.categories || []).reduce((a, c) => a + (c.tours_completed || 0), 0);
                      const wd = (w.categories || []).reduce((a, c) => a + (c.won_deals || 0), 0);
                      const dealCount = (w.closed_deals || []).length;
                      const dealMrr = (w.closed_deals || []).reduce((a, d) => a + (d.monthly_price || 0), 0);
                      return (
                        <tr key={w.id} data-testid={`weekly-row-${w.week_start}`} className="border-b border-brand-border last:border-0 hover:bg-brand-hover">
                          <td className="px-6 py-3 text-brand-ink align-top">
                            {fmtWeek(w.week_start)}
                            {w.confirmed_by && <p className="text-[10px] text-brand-tertiary font-normal mt-0.5" data-testid={`weekly-confirmed-${w.week_start}`}>{w.source === "crm" ? "From CRM · " : ""}confirmed by {w.confirmed_by}</p>}
                            {w.notes && <p data-testid={`weekly-notes-${w.week_start}`} className="text-[11px] text-brand-tertiary font-normal mt-1 max-w-[260px] whitespace-normal leading-snug">{w.notes}</p>}
                          </td>
                          <td className="px-4 py-3 text-right text-brand-muted">{fmtPeriod(w.period)}</td>
                          <td className="px-4 py-3 text-right font-mono text-brand-muted">{tb}</td>
                          <td className="px-4 py-3 text-right font-mono text-brand-muted">{tc}</td>
                          <td className="px-4 py-3 text-right font-mono text-brand-pos font-medium">{wd}</td>
                          <td className="px-4 py-3 text-right font-mono text-brand-ink" data-testid={`weekly-deals-${w.week_start}`}>{dealCount ? `${dealCount} · ${fmtCurrency(dealMrr)}/mo` : "—"}</td>
                          <td className="px-4 py-3 text-right font-mono text-brand-ink">{w.new_business_revenue != null ? fmtCurrency(w.new_business_revenue) : "—"}</td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <button onClick={() => setViewingWeek(w)} data-testid={`weekly-view-${w.week_start}`} className="p-1.5 text-brand-tertiary hover:text-brand-ink"><Eye className="h-4 w-4" /></button>
                            <button onClick={() => setEditingWeek({ week: w, isNew: false })} data-testid={`weekly-edit-${w.week_start}`} className="p-1.5 text-brand-tertiary hover:text-brand-primary"><Pencil className="h-4 w-4" /></button>
                            <button onClick={() => removeWeek(w)} data-testid={`weekly-delete-${w.week_start}`} className="p-1.5 text-brand-tertiary hover:text-brand-neg"><Trash2 className="h-4 w-4" /></button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="px-6 py-3 border-t border-brand-border">
                  <button onClick={toggleEarlierWeeks} disabled={loadingEarlier} data-testid="weekly-toggle-earlier"
                    className="inline-flex items-center gap-1.5 text-xs text-brand-muted hover:text-brand-primary transition-colors disabled:opacity-60">
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showEarlierWeeks ? "rotate-180" : ""}`} />
                    {loadingEarlier ? "Loading…" : showEarlierWeeks ? "Hide earlier weeks" : "Show earlier weeks"}
                    <span className="text-brand-tertiary">· already rolled into Monthly Detail</span>
                  </button>
                  {showEarlierWeeks && earlierWeeks.length === 0 && !loadingEarlier && (
                    <span className="ml-2 text-xs text-brand-tertiary">No earlier weeks recorded.</span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Deal Pipeline — all closed deals for this location across weeks */}
        {canEdit && specificLoc && (
          <div className="bg-brand-surface border border-brand-border" data-testid="deal-pipeline-section">
            <div className="px-6 py-4 border-b border-brand-border flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <UserCheck className="h-4 w-4 text-brand-primary" />
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-brand-tertiary">Closed Deal Pipeline <span className="text-brand-gold">· staff only</span></p>
                  <p className="font-serif text-lg text-brand-ink">{location} — {dealsTotals.count} {dealsTotals.count === 1 ? "deal" : "deals"} · <span className="font-mono text-brand-pos">{fmtCurrency(dealsTotals.mrr)}/mo</span></p>
                  <p className="text-[11px] text-brand-tertiary mt-0.5">Showing {fmtPeriod(priorMonthStr)} &amp; {fmtPeriod(currentMonthStr)}; earlier months are rolled up below.</p>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-brand-tertiary border-b border-brand-border">
                    {["Closed (week)", "Company", "Office", "Product", "Price / mo", "Term"].map((h, i) => (
                      <th key={h} className={`font-medium uppercase tracking-[0.1em] text-[11px] px-4 py-3 ${i === 0 || i === 1 ? "text-left px-6" : i === 4 ? "text-right" : "text-left"}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dealsTotals.count === 0 && (<tr><td colSpan={6} className="px-6 py-8 text-center text-brand-tertiary">No closed deals logged for {location} yet. Add them in a weekly entry.</td></tr>)}
                  {dealsTotals.count > 0 && recentDeals.length === 0 && (<tr><td colSpan={6} className="px-6 py-6 text-center text-brand-tertiary">No deals in the last two months — expand an earlier month below.</td></tr>)}
                  {recentDeals.map((d, i) => (
                    <tr key={`${d.week_start}-${i}`} data-testid={`deal-pipeline-row-${i}`} className="border-b border-brand-border last:border-0 hover:bg-brand-hover">
                      <td className="px-6 py-3 text-brand-muted whitespace-nowrap">{fmtWeek(d.week_start)}</td>
                      <td className="px-6 py-3 text-brand-ink">{d.company}</td>
                      <td className="px-4 py-3 text-brand-muted">{d.office_number || "—"}</td>
                      <td className="px-4 py-3 text-brand-muted">{d.product || "—"}</td>
                      <td className="px-4 py-3 text-right font-mono text-brand-pos">{d.monthly_price ? fmtCurrency(d.monthly_price) : "—"}</td>
                      <td className="px-4 py-3 text-brand-muted">{d.term || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <MonthlyRollup testid="deal-pipeline-rollup" months={olderDealMonths} leftCols={2} fetchDetail={loadDealDetail}
              columns={["Closed (week)", "Company", "Office", "Product", "Price / mo", "Term"]}
              renderSummary={(mth) => (<span className="text-brand-muted">{mth.count} {mth.count === 1 ? "deal" : "deals"} · <span className="font-mono text-brand-pos">{fmtCurrency(mth.mrr)}/mo</span></span>)}
              renderRow={(d, i) => (
                <tr key={`${d.week_start}-${i}`} data-testid={`deal-rollup-row-${d.week_start}-${i}`} className="border-b border-brand-border last:border-0">
                  <td className="px-6 py-2.5 text-brand-muted whitespace-nowrap">{fmtWeek(d.week_start)}</td>
                  <td className="px-4 py-2.5 text-brand-ink">{d.company}</td>
                  <td className="px-4 py-2.5 text-right text-brand-muted">{d.office_number || "—"}</td>
                  <td className="px-4 py-2.5 text-right text-brand-muted">{d.product || "—"}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-brand-pos">{d.monthly_price ? fmtCurrency(d.monthly_price) : "—"}</td>
                  <td className="px-4 py-2.5 text-right text-brand-muted">{d.term || "—"}</td>
                </tr>
              )} />
          </div>
        )}


        {/* Move-out / notice tracker */}
        {canEdit ? (
          <div className="bg-brand-surface border border-brand-border" data-testid="moveout-section">
            <div className="px-6 py-4 border-b border-brand-border flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <DoorOpen className="h-4 w-4 text-brand-neg" />
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-brand-tertiary">Move-Outs / Notices <span className="text-brand-gold">· staff only</span></p>
                  <p className="font-serif text-lg text-brand-ink">
                    {mo.count} upcoming · <span className="text-brand-neg font-mono">{fmtCurrency(mo.monthly_at_risk)}/mo</span> at risk
                  </p>
                </div>
              </div>
              <button onClick={() => setEditingMoveOut({ mo: null, isNew: true })} data-testid="moveout-add"
                className="flex items-center gap-2 bg-brand-primary text-brand-page px-4 py-2 text-sm hover:bg-brand-primaryHover active:scale-[0.98] transition-all">
                <Plus className="h-4 w-4" /> Add Notice
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-brand-tertiary border-b border-brand-border">
                    {["Company", "Office", "Desks", "Location", "Notice", "Vacating", "Monthly $", "Resold?", ""].map((h, i) => (
                      <th key={h || "mo-act"} className={`font-medium uppercase tracking-[0.1em] text-[11px] px-4 py-3 ${i === 0 ? "text-left px-6" : i === 7 ? "text-center" : "text-right"}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {upcomingMoveOuts.length === 0 && (<tr><td colSpan={9} className="px-6 py-8 text-center text-brand-tertiary">{(upcomingMoveOuts.length + vacatedTotals.count) === 0 ? "No move-out notices recorded." : "No upcoming move-outs — past notices are hidden once their vacate date passes."}</td></tr>)}
                  {upcomingMoveOuts.map((m) => {
                    const upcoming = (m.vacate_date || "") >= todayStr;
                    const matches = matchesFor(m);
                    return (
                      <tr key={m.id} data-testid={`moveout-row-${m.id}`} className="border-b border-brand-border last:border-0 hover:bg-brand-hover align-top">
                        <td className="px-6 py-3 text-brand-ink">
                          {m.company}
                          {upcoming && matches.length > 0 && (
                            <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid={`moveout-matches-${m.id}`}>
                              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] text-brand-pos bg-brand-pos/10 rounded-full px-2 py-0.5">
                                <UserCheck className="h-3 w-3" /> {matches.length} waitlist match{matches.length > 1 ? "es" : ""}
                              </span>
                              {matches.slice(0, 3).map((w) => (
                                <span key={w.id} className="text-[11px] text-brand-muted">
                                  {w.name}{w.kind === "member" && w.direction ? ` (${w.direction})` : w.kind === "tour" ? " (tour)" : ""}
                                </span>
                              ))}
                              {matches.length > 3 && <span className="text-[11px] text-brand-tertiary">+{matches.length - 3}</span>}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-brand-muted">{m.office_number || "—"}</td>
                        <td className="px-4 py-3 text-right font-mono text-brand-muted">{m.desks != null ? m.desks : "—"}</td>
                        <td className="px-4 py-3 text-right text-brand-muted">{m.location}</td>
                        <td className="px-4 py-3 text-right text-brand-muted">{m.notice_date || "—"}</td>
                        <td className={`px-4 py-3 text-right font-mono ${upcoming ? "text-brand-neg" : "text-brand-tertiary"}`}>{m.vacate_date}</td>
                        <td className="px-4 py-3 text-right font-mono text-brand-ink">{fmtCurrency(m.monthly_rent)}</td>
                        <td className="px-4 py-3 text-center">
                          <input type="checkbox" checked={!!m.resold} onChange={() => toggleResold(m)} data-testid={`moveout-resold-${m.id}`}
                            className="h-4 w-4 accent-brand-pos cursor-pointer" title={m.resold ? "Resold" : "Mark as resold"} />
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <button onClick={() => setEditingMoveOut({ mo: m, isNew: false })} data-testid={`moveout-edit-${m.id}`} className="p-1.5 text-brand-tertiary hover:text-brand-primary"><Pencil className="h-4 w-4" /></button>
                          <button onClick={() => removeMoveOut(m)} data-testid={`moveout-delete-${m.id}`} className="p-1.5 text-brand-tertiary hover:text-brand-neg"><Trash2 className="h-4 w-4" /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {vacatedTotals.count > 0 && (
              <div className="border-t border-brand-border" data-testid="churn-recap">
                <button onClick={() => setShowChurn((v) => !v)} data-testid="churn-toggle"
                  className="w-full flex items-center justify-between gap-3 px-6 py-3.5 text-left hover:bg-brand-hover transition-colors">
                  <span className="inline-flex items-center gap-2 text-sm text-brand-ink">
                    <RotateCcw className="h-4 w-4 text-brand-tertiary" />
                    Recently vacated · <span className="text-brand-muted">{vacatedTotals.count} {vacatedTotals.count === 1 ? "company" : "companies"}</span>
                    <span className="font-mono text-brand-neg">{fmtCurrency(vacatedTotals.mrr)}/mo churned</span>
                    {vacatedTotals.resold > 0 && <span className="text-[11px] text-brand-pos">· {vacatedTotals.resold} resold</span>}
                  </span>
                  <ChevronDown className={`h-4 w-4 text-brand-tertiary transition-transform ${showChurn ? "rotate-180" : ""}`} />
                </button>
                {showChurn && (
                  <>
                  <div className="overflow-x-auto border-t border-brand-border">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-brand-tertiary border-b border-brand-border">
                          {["Company", "Office", "Location", "Vacated", "Monthly $", "Resold?"].map((h, i) => (
                            <th key={h} className={`font-medium uppercase tracking-[0.1em] text-[11px] px-4 py-3 ${i === 0 ? "text-left px-6" : i === 5 ? "text-center" : "text-right"}`}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {recentVacated.length === 0 && (<tr><td colSpan={6} className="px-6 py-5 text-center text-brand-tertiary">Nothing vacated in the last two months — expand an earlier month below.</td></tr>)}
                        {recentVacated.map((m) => (
                          <tr key={m.id} data-testid={`churn-row-${m.id}`} className="border-b border-brand-border last:border-0 hover:bg-brand-hover">
                            <td className="px-6 py-3 text-brand-muted">{m.company}</td>
                            <td className="px-4 py-3 text-right text-brand-muted">{m.office_number || "—"}</td>
                            <td className="px-4 py-3 text-right text-brand-muted">{m.location}</td>
                            <td className="px-4 py-3 text-right font-mono text-brand-tertiary">{m.vacate_date}</td>
                            <td className="px-4 py-3 text-right font-mono text-brand-muted">{fmtCurrency(m.monthly_rent)}</td>
                            <td className="px-4 py-3 text-center">
                              {m.resold
                                ? <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] text-brand-pos bg-brand-pos/10 rounded-full px-2 py-0.5">Resold</span>
                                : <span className="text-[11px] text-brand-tertiary">—</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <MonthlyRollup testid="churn-rollup" months={olderVacatedMonths} fetchDetail={loadMoDetail}
                    columns={["Company", "Office", "Location", "Vacated", "Monthly $", "Resold?"]}
                    renderSummary={(mth) => (<span className="text-brand-muted">{mth.count} {mth.count === 1 ? "company" : "companies"} · <span className="font-mono text-brand-neg">{fmtCurrency(mth.mrr)}/mo</span>{mth.resold > 0 && <span className="text-[11px] text-brand-pos"> · {mth.resold} resold</span>}</span>)}
                    renderRow={(m) => (
                      <tr key={m.id} data-testid={`churn-rollup-row-${m.id}`} className="border-b border-brand-border last:border-0">
                        <td className="px-6 py-2.5 text-brand-muted">{m.company}</td>
                        <td className="px-4 py-2.5 text-right text-brand-muted">{m.office_number || "—"}</td>
                        <td className="px-4 py-2.5 text-right text-brand-muted">{m.location}</td>
                        <td className="px-4 py-2.5 text-right font-mono text-brand-tertiary">{m.vacate_date}</td>
                        <td className="px-4 py-2.5 text-right font-mono text-brand-muted">{fmtCurrency(m.monthly_rent)}</td>
                        <td className="px-4 py-2.5 text-center">
                          {m.resold
                            ? <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] text-brand-pos bg-brand-pos/10 rounded-full px-2 py-0.5">Resold</span>
                            : <span className="text-[11px] text-brand-tertiary">—</span>}
                        </td>
                      </tr>
                    )} />
                  </>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="bg-brand-surface border border-brand-border px-6 py-5 flex items-center gap-4" data-testid="moveout-summary">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-neg/10 text-brand-neg"><AlertTriangle className="h-5 w-5" /></span>
            <div>
              <p className="text-xs uppercase tracking-[0.15em] text-brand-tertiary">Upcoming Move-Outs</p>
              <p className="font-serif text-xl text-brand-ink"><span className="font-mono text-brand-neg">{fmtCurrency(mo.monthly_at_risk)}/mo</span> at risk across {mo.count} {mo.count === 1 ? "company" : "companies"}</p>
            </div>
          </div>
        )}

        {/* Waitlists — regional/admin only, per location */}
        {canEdit && (
          <>
            <WaitlistSection kind="member" entries={memberWaitlist} location={location} specificLoc={specificLoc}
              onAdd={() => setEditingWaitlist({ entry: null, isNew: true, kind: "member" })}
              onEdit={(w) => setEditingWaitlist({ entry: w, isNew: false, kind: "member" })}
              onRemove={openRemoveWaitlist} onRestore={restoreWaitlist} onHardDelete={purgeWaitlist} />
            <WaitlistSection kind="tour" entries={tourWaitlist} location={location} specificLoc={specificLoc}
              onAdd={() => setEditingWaitlist({ entry: null, isNew: true, kind: "tour" })}
              onEdit={(w) => setEditingWaitlist({ entry: w, isNew: false, kind: "tour" })}
              onRemove={openRemoveWaitlist} onRestore={restoreWaitlist} onHardDelete={purgeWaitlist} />
          </>
        )}
        </>)}
      </main>

      {editing && (
        <Editor initial={editing.month} isNew={editing.isNew} defaultLocation={defaultEditLocation} locations={meta.locations}
          onClose={() => setEditing(null)} onSaved={() => { load(); loadMeta(); }} />
      )}
      {editingWeek && (
        <WeeklyEditor initial={editingWeek.week} isNew={editingWeek.isNew} location={location}
          onClose={() => setEditingWeek(null)} onSaved={() => { loadWeekly(); loadDeals(); load(); loadMeta(); }} />
      )}
      {viewingWeek && (
        <WeekView week={viewingWeek}
          onClose={() => setViewingWeek(null)}
          onEdit={() => { setEditingWeek({ week: viewingWeek, isNew: false }); setViewingWeek(null); }} />
      )}
      {editingMoveOut && (
        <MoveOutEditor initial={editingMoveOut.mo} isNew={editingMoveOut.isNew} locations={meta.locations} defaultLocation={defaultEditLocation}
          onClose={() => setEditingMoveOut(null)} onSaved={() => { loadMoveOuts(); load(); }} />
      )}
      {editingWaitlist && (
        <WaitlistEditor initial={editingWaitlist.entry} isNew={editingWaitlist.isNew} kind={editingWaitlist.kind} location={location}
          onClose={() => setEditingWaitlist(null)} onSaved={loadWaitlist} />
      )}
      {removingWaitlist && (
        <WaitlistRemoveModal entry={removingWaitlist}
          onClose={() => setRemovingWaitlist(null)} onConfirm={confirmRemoveWaitlist} />
      )}
    </div>
  );
}
